import { type JSX, Match, Show, Switch } from 'solid-js';
import { t } from '../i18n';
import { type ReviewBrain, type ReviewProvenance, reviewBlindGapKey } from '../lib/repo-reviews';
import { StatusPill } from './StatusPill';

// Did a model review this diff — and what did the box actually drive?
//
// TWO QUESTIONS, ANSWERED SEPARATELY, and that separation is the component's whole
// reason to exist. `brain` is what was RESOLVED for the run; `provenance` is what
// the box REPORTED driving. Neither is inferred from the other: an absent
// attribution is honest, an inferred one credits a model that may never have seen
// the diff.
//
// THREE BRAIN STATES, RENDERED DISTINCTLY — never two.
//   * `model`    → a credential resolved. Named when the run recorded provider +
//                  model id, and said to be unattributed when it did not: a run
//                  written before those fields existed has a missing NAME, not a
//                  missing model.
//   * `blind`    → the brain reported no model, with the gap it named. A WARNING,
//                  not an error, because that is what the platform actually did:
//                  a blind review settles its check `neutral` and says so
//                  (`packages/review/src/check.ts` — never `failure`). This badge
//                  REPORTS that settlement; it does not re-decide it.
//   * `unknown`  → no brain event on the run at all. Its own pill and its own
//                  sentence, because folding it into `blind` mis-reports a run
//                  that never got that far as one that ran without a model — the
//                  exact collapse that cost an operator view 5 of 92 runs.

export type ReviewBrainBadgeProps = {
  readonly brain: ReviewBrain;
  /** Null on every run whose box recorded none. That null is information, not a gap to hide. */
  readonly provenance: ReviewProvenance | null;
};

/** The named half of a `model` verdict, or null when the run recorded no name. */
function attribution(brain: ReviewBrain): { model: string; provider: string } | null {
  if (brain.kind !== 'model') return null;
  if (brain.model_id === null || brain.provider === null) return null;
  return { model: brain.model_id, provider: brain.provider };
}

export function ReviewBrainBadge(props: ReviewBrainBadgeProps): JSX.Element {
  return (
    <div class="review-brain">
      <Switch>
        <Match when={props.brain.kind === 'model'}>
          <StatusPill tone="success">{t('review_brain.model')}</StatusPill>
          <Show
            when={attribution(props.brain)}
            fallback={<p class="review-brain__detail">{t('review_brain.model_unattributed')}</p>}
          >
            {(named) => (
              <p class="review-brain__detail">
                {t('review_brain.model_attribution', {
                  model: named().model,
                  provider: named().provider,
                })}
              </p>
            )}
          </Show>
        </Match>

        <Match when={props.brain.kind === 'blind' ? props.brain : null}>
          {(blind) => (
            <>
              <StatusPill tone="warning">{t('review_brain.blind')}</StatusPill>
              <p class="review-brain__detail">{t(reviewBlindGapKey(blind().gap))}</p>
              <p class="review-brain__detail">{t('review_brain.blind_warning')}</p>
            </>
          )}
        </Match>

        <Match when={props.brain.kind === 'unknown'}>
          <StatusPill tone="neutral">{t('review_brain.unknown')}</StatusPill>
          <p class="review-brain__detail">{t('review_brain.unknown_detail')}</p>
        </Match>
      </Switch>

      {/* Reported BESIDE the verdict above, never merged into it, and always
          present: "the box recorded no provenance" is an answer to a different
          question, and hiding the row would read as "we did not ask". */}
      <p class="review-brain__provenance">
        <span class="review-brain__provenance-label">{t('review_brain.provenance_title')}</span>{' '}
        <Show
          when={props.provenance}
          fallback={
            <span class="review-brain__provenance-absent">
              {t('review_brain.provenance_absent')}
            </span>
          }
        >
          {(prov) => (
            <span class="review-brain__provenance-value">
              {t('review_brain.provenance_value', {
                model: prov().model,
                provider: prov().provider,
              })}
              <Show when={prov().served_model}>
                {(served) => <> {t('review_brain.provenance_served', { served: served() })}</>}
              </Show>{' '}
              {/* The two provider names on this card are spelled differently and
                  BOTH are right: the brain row carries the BYOK key's provider
                  slug (`anthropic`, `openrouter`), this row the host the box
                  actually dialled (`api.anthropic.com`, `openrouter.ai`). They
                  never coincide — 16 of 16 slugs in `PROVIDER_WIRE` differ from
                  the host `providerFromBaseUrl` derives — so a reader two lines
                  apart has no way to tell "complementary" from "contradictory"
                  unless the card says which is which. This clause is that, and it
                  is the only place it is said to a dashboard reader. */}
              <span class="review-brain__provenance-note">
                {t('review_brain.provenance_endpoint')}
              </span>
            </span>
          )}
        </Show>
      </p>
    </div>
  );
}
